import { AxiosResponse } from 'axios'
import {
  SupportUser,
  SupportUserMutableModel,
  EmptyApiResult,
  Guid,
  ApiResult,
  SupportUserPermission,
} from '../api'
import { useApiClient } from './useApiClient'
import { createFormDataFromModel } from 'utils'

export type AccessLevel = 'View' | 'Manage' | 'None'

export type PermissionDefinition = {
  manage: SupportUserPermission
  view: SupportUserPermission
  label: string
}

export type PermissionMapping = {
  accessDiagnosticConfiguration: PermissionDefinition
  accessSupportUsers: PermissionDefinition
  accessApiToken: PermissionDefinition
  accessInterfaceConfiguration: PermissionDefinition
  accessDatabaseConfiguration: PermissionDefinition
  viewDocuments: PermissionDefinition
  viewDatabases: PermissionDefinition
  accessBlueGreenConfiguration: PermissionDefinition
  manageAllApiTokens: PermissionDefinition
  renewCertificate: PermissionDefinition
}

export type PermissionType = keyof PermissionMapping

export const permissionMapping: PermissionMapping = {
  accessDiagnosticConfiguration: {
    manage: SupportUserPermission.ManageDiagnosticConfiguration,
    view: SupportUserPermission.ViewDiagnosticConfiguration,
    label: 'Diagnostic Configuration',
  },
  accessSupportUsers: {
    manage: SupportUserPermission.ManageSupportUsers,
    view: SupportUserPermission.ViewSupportUsers,
    label: 'Support Users',
  },
  accessApiToken: {
    manage: SupportUserPermission.ManageApiToken,
    view: SupportUserPermission.ViewApiToken,
    label: 'Api Token',
  },
  accessInterfaceConfiguration: {
    manage: SupportUserPermission.ManageInterfaceConfiguration,
    view: SupportUserPermission.ViewInterfaceConfiguration,
    label: 'Interface Configuration',
  },
  accessDatabaseConfiguration: {
    manage: SupportUserPermission.ManageDatabaseConfiguration,
    view: SupportUserPermission.ViewDatabaseConfiguration,
    label: 'Database Configuration',
  },
  viewDocuments: {
    view: SupportUserPermission.ViewDocuments,
    manage: SupportUserPermission.ViewDocuments,
    label: 'View Customer Documents',
  },
  viewDatabases: {
    view: SupportUserPermission.ViewShards,
    manage: SupportUserPermission.ViewShards,
    label: 'View Databases',
  },
  accessBlueGreenConfiguration: {
    view: SupportUserPermission.ViewBlueGreenConfiguration,
    manage: SupportUserPermission.ManageBlueGreenConfiguration,
    label: 'BlueGreen Configuration',
  },
  manageAllApiTokens: {
    manage: SupportUserPermission.ManageAllApiTokens,
    view: SupportUserPermission.ManageAllApiTokens,
    label: 'Manage All Api Token',
  },
  renewCertificate: {
    manage: SupportUserPermission.RenewCertificate,
    view: SupportUserPermission.RenewCertificate,
    label: 'Renew Certificate',
  },
}

export type SupportUserPermissionOnly = {
  [key in PermissionType]: AccessLevel
}

export type SupportUserSpreadPermissions = SupportUser &
  SupportUserPermissionOnly

export const checkSupportUserPermission = (
  supportUser: SupportUser,
  permission: PermissionType,
): AccessLevel => {
  return supportUser.permissions.includes(permissionMapping[permission].manage)
    ? 'Manage'
    : supportUser.permissions.includes(permissionMapping[permission].view)
    ? 'View'
    : 'None'
}

export const checkAllSupportUserPermissions = (
  supportUser: SupportUser,
): SupportUserPermissionOnly => {
  return Object.keys(permissionMapping).reduce((accum, curr) => {
    const permissionType = curr as PermissionType
    return {
      ...accum,
      [curr]: checkSupportUserPermission(supportUser, permissionType),
    } as SupportUserPermissionOnly
  }, {} as SupportUserPermissionOnly)
}

export const mapAccessLevelsToPermissions = (
  supportUser: SupportUserSpreadPermissions,
) => {
  const permissions: SupportUserPermission[] = []
  Object.keys(permissionMapping).forEach((permission) => {
    const permissionType = permission as PermissionType

    if (supportUser[permissionType] === 'View') {
      permissions.unshift(permissionMapping[permissionType].view)
    }

    if (supportUser[permissionType] === 'Manage') {
      permissions.unshift(permissionMapping[permissionType].view)

      permissions.unshift(permissionMapping[permissionType].manage)
    }
  })

  return permissions
}

export const useSupportUserApi = () => {
  const apiClient = useApiClient()

  const getSupportUsers = (includeDisabled = true) => {
    return apiClient.request<void, AxiosResponse<ApiResult<SupportUser[]>>>({
      url: `api/supportUsers?includeDisabled=${includeDisabled}`,
      method: 'get',
      withCredentials: true,
    })
  }

  const getSupportUser = (supportUserId: Guid) => {
    return apiClient.request<void, AxiosResponse<SupportUser>>({
      url: `api/supportUsers/${supportUserId}`,
      method: 'get',
      withCredentials: true,
    })
  }

  const editSupportUser = (
    supportUserId: Guid,
    values: SupportUserMutableModel,
  ) => {
    const formData = createFormDataFromModel(values)

    return apiClient.request<void, AxiosResponse<ApiResult<SupportUser>>>({
      url: `api/supportUsers/${supportUserId}`,
      method: 'put',
      data: formData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      withCredentials: true,
    })
  }

  const deleteSupportUser = (supportUserId: Guid) => {
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: `api/supportUsers/${supportUserId}`,
      method: 'delete',
      withCredentials: true,
    })
  }

  return {
    getSupportUser,
    getSupportUsers,
    editSupportUser,
    deleteSupportUser,
  }
}
